import { DropDownList, DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';
import {
  GridCellProps,
  GridColumnMenuFilter,
  GridColumnMenuFilterUIProps,
  GridHeaderCellProps,
} from '@progress/kendo-react-grid';
import { format } from 'date-fns';
import React, { useMemo, useState } from 'react';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { createPortal } from 'react-dom';
import GridCellBase from 'src/components/GridCellBase';
import { GridColumn } from 'src/components/GridWrapper';
import MessageAttachmentsModal from 'src/components/MessageAttachmentsModal';
import { MESSAGE_COLORS } from 'src/constants/colors';
import useViewMessage from 'src/hooks/useViewMessage';
import MessageSubject from 'src/sections/view_message/view_subject_cell/MessageSubject';
import { Message } from 'src/services/messages_services/useGetMessageApi';

// Custom header for attachments NO sort arrows (prevent double sort arrows)
const AttachmentHeaderNoSort = (props: GridHeaderCellProps) => (
  <span className="k-cell-inner" title={'Attachments'}>
    <span className="k-link w-fit" onClick={props.onClick}>
      <span className="k-column-title k-icon k-i-attachment k-i-clip" />
    </span>
  </span>
);

const AttachmentsCountCell = (props: GridCellProps) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const { attachmentsCount, messageId } = props.dataItem;

  const handleAttachmentsClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (attachmentsCount > 0) {
      setIsModalVisible(true);
    }
  };

  const closeModal = () => {
    setIsModalVisible(false);
  };

  return (
    <>
      <GridCellBase {...props}>
        <span style={{ display: 'block', textAlign: 'center', width: '100%' }}>
          {attachmentsCount > 0 ? (
            <button
              onClick={handleAttachmentsClick}
              style={{
                color: MESSAGE_COLORS.LINK_BLUE,
                textDecoration: 'underline',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: 'inherit',
              }}
              title={`View ${attachmentsCount} attachment(s) for message ${messageId}`}
            >
              {attachmentsCount}
            </button>
          ) : (
            attachmentsCount
          )}
        </span>
      </GridCellBase>

      {/* Render Modal using Portal to prevent grid layout issues */}
      {isModalVisible &&
        createPortal(
          <MessageAttachmentsModal
            visible={isModalVisible}
            closeModal={closeModal}
            messageId={messageId}
          />,
          document.body
        )}
    </>
  );
};

// Custom Status Filter Component for Inbox
const StatusFilterUI = (filterUIProps: GridColumnMenuFilterUIProps) => {
  const { value, onChange } = filterUIProps.firstFilterProps;
  const { messages } = useViewMessage();

  // Get unique status values from messages
  const statusOptions = useMemo(() => {
    if (!messages || messages.length === 0) return [{ text: 'All', value: '' }];

    const uniqueStatuses = [
      ...new Set(
        messages
          .map((msg: Message) => msg.decodedStatus)
          .filter((status: string) => status && status.trim() !== '')
      ),
    ].sort();

    return [
      { text: 'All', value: '' },
      ...uniqueStatuses.map((status) => ({ text: status, value: status })),
    ];
  }, [messages]);

  const handleDropDownChange = (event: DropDownListChangeEvent) => {
    const selectedValue = event.target.value?.value || '';
    onChange({
      value: selectedValue,
      operator: selectedValue ? 'eq' : '',
      syntheticEvent: event.syntheticEvent,
    });
  };

  const currentValue = statusOptions.find((option) => option.value === value) || statusOptions[0];

  return (
    <DropDownList
      data={statusOptions}
      textField="text"
      dataItemKey="value"
      value={currentValue}
      onChange={handleDropDownChange}
      style={{ width: '100%' }}
    />
  );
};

// Custom Status Filter Cell for Inbox
const StatusFilterCell = (columnMenuProps: any) => (
  <GridColumnMenuFilter
    {...columnMenuProps}
    expanded={true}
    hideSecondFilter
    filterUI={(filterUIProps: GridColumnMenuFilterUIProps) => <StatusFilterUI {...filterUIProps} />}
  />
);

// Custom Boolean Filter Component for isDeleted
const BooleanFilterUI = (filterUIProps: GridColumnMenuFilterUIProps) => {
  const { value, onChange } = filterUIProps.firstFilterProps;

  const booleanOptions = [
    { text: 'Show All', value: '' },
    { text: 'Show Deleted', value: 'true' },
    { text: 'Show All Active', value: 'false' },
  ];

  const handleDropDownChange = (event: DropDownListChangeEvent) => {
    const selectedValue = event.target.value?.value || '';
    let filterValue = selectedValue;
    let operator = '';

    if (selectedValue === 'true') {
      filterValue = true;
      operator = 'eq';
    } else if (selectedValue === 'false') {
      filterValue = false;
      operator = 'eq';
    }

    onChange({
      value: filterValue,
      operator: operator,
      syntheticEvent: event.syntheticEvent,
    });
  };

  const currentValue =
    booleanOptions.find((option) => {
      if (value === true) return option.value === 'true';
      if (value === false) return option.value === 'false';
      return option.value === '';
    }) || booleanOptions[0];

  return (
    <DropDownList
      data={booleanOptions}
      textField="text"
      dataItemKey="value"
      value={currentValue}
      onChange={handleDropDownChange}
      style={{ width: '150px' }}
    />
  );
};

// Custom Boolean Filter Cell
const BooleanFilterCell = (columnMenuProps: any) => (
  <GridColumnMenuFilter
    {...columnMenuProps}
    expanded={true}
    hideSecondFilter
    filterUI={(filterUIProps: GridColumnMenuFilterUIProps) => (
      <BooleanFilterUI {...filterUIProps} />
    )}
  />
);

// Custom Priority Filter Component for decodedPriority
const PriorityFilterUI = (filterUIProps: GridColumnMenuFilterUIProps) => {
  const { value, onChange } = filterUIProps.firstFilterProps;
  const { messages } = useViewMessage();

  // Get unique priority values from messages
  const priorityOptions = useMemo(() => {
    if (!messages || messages.length === 0) return [];

    const uniquePriorities = [
      ...new Set(
        messages
          .map((msg: Message) => msg.decodedPriority)
          .filter(
            (priority: string | undefined): priority is string =>
              priority !== undefined && priority.trim() !== ''
          )
      ),
    ].sort();

    return [
      { text: 'Show All Active', value: '' },
      ...uniquePriorities.map((priority) => ({ text: priority, value: priority })),
    ];
  }, [messages]);

  const handleDropDownChange = (event: DropDownListChangeEvent) => {
    const selectedValue = event.target.value?.value || '';
    onChange({
      value: selectedValue,
      operator: selectedValue ? 'eq' : '',
      syntheticEvent: event.syntheticEvent,
    });
  };

  const currentValue =
    priorityOptions.find((option) => option.value === value) || priorityOptions[0];

  return (
    <DropDownList
      data={priorityOptions}
      textField="text"
      dataItemKey="value"
      value={currentValue}
      onChange={handleDropDownChange}
      style={{ width: '150px' }}
    />
  );
};

// Custom Priority Filter Cell
const PriorityFilterCell = (columnMenuProps: any) => (
  <GridColumnMenuFilter
    {...columnMenuProps}
    expanded={true}
    hideSecondFilter
    filterUI={(filterUIProps: GridColumnMenuFilterUIProps) => (
      <PriorityFilterUI {...filterUIProps} />
    )}
  />
);

export const useMessageColumnInbox = () => {
  const { hasEditMessagePermission, t } = useViewMessage();

  //Added to set column widths based on grid size
  const [gridSize, setGridSize] = useState(() => window.innerWidth);

  const columnFormat1Size = useMemo(() => {
    // Calculate available width more conservatively to prevent horizontal scroll
    const availableWidth = Math.min(gridSize, 1900);
    const baseWidth = availableWidth >= 1450 ? (availableWidth - 400) * 0.08 : 100;
    return Math.max(baseWidth, 100); // Minimum 100px
  }, [gridSize]);

  //Note: INBOX grid does NOT Recipient ("To") or Sender ("From") columns
  const viewMessageInboxColumns: GridColumn<any>[] = useMemo(
    () => [
      ...(hasEditMessagePermission
        ? ([
            {
              field: 'selected',
              title: '',
              menu: false,
              // Note: selectedRows & selectedDelRows are calculated in parent (ViewMessageTable)
            },
          ] as const)
        : []),

      {
        field: 'decodedPriority',
        title: t('Priority'),
        filter: 'text',
        filterable: true,
        sortable: true,
        width: 60,
        className: 'text-left',
        columnMenu: PriorityFilterCell,
        component: (props: GridCellProps) => {
          const priority = props.dataItem.decodedPriority;
          const getPriorityColor = (priority: string) => {
            const priorityLower = priority?.toLowerCase() || '';
            if (priorityLower === 'urgent') return 'firebrick';
            if (priorityLower === 'high') return '#e66b00';
            if (priorityLower === 'low') return '#1e90ff';
            return 'black'; // Normal or default
          };
          return (
            <GridCellBase {...props}>
              <span
                style={{
                  display: 'block',
                  textAlign: 'left',
                  width: '100%',
                  color: getPriorityColor(priority),
                }}
              >
                {priority}
              </span>
            </GridCellBase>
          );
        },
      },

      /* ------------------------------------------------------
      HIDE AttachmentsCount column (will re-add later for Phase Two)
      {
        field: 'attachmentsCount',
        //title: '📎',
        filterable: false, menu: false, sortable: true, width: 30, resizable: false, className: 'text-center attachments-header',
        headerCell: AttachmentHeaderNoSort, component: AttachmentsCountCell,
      },
      --------------------------------------------------------*/

      {
        field: 'messageId',
        title: t('Message ID'),
        filter: 'text',
        sortable: true,
        filterable: true,
        width: columnFormat1Size - 50,
        show: false,
      },

      /* ------------------------------------------------------
      HIDE ClientId column 
      //temporarily added ClientId for visibility while testing
       { field: 'clientId', title: t('Client ID'),
         filter: 'text', sortable: true, filterable: true, width: 80,
        },
      --------------------------------------------------------*/

      /* -----HIDE Deleted (Resp) column ------------------------- 
      {
        field: 'isDeleted',
        title: t('Deleted (Resp)'),
        filter: 'boolean',
        filterable: true,
        columnMenu: BooleanFilterCell,
        sortable: true,
        width: 135,
        className: 'text-center',
        hidden: false,
        component: (props: GridCellProps) => (
          <GridCellBase {...props}>
            <span
              style={{
                display: 'block',
                textAlign: 'center',
                width: '100%',
                color: props.dataItem.isDeleted ? 'firebrick' : 'inherit',
                backgroundColor: props.dataItem.isDeleted ? MESSAGE_COLORS.DELETED_STATUS_BG : MESSAGE_COLORS.TRANSPARENT,
                padding: props.dataItem.isDeleted ? '2px 4px' : '0',
                borderRadius: props.dataItem.isDeleted ? '3px' : '0',
              }}
            >
              {props.dataItem.isDeleted ? 'Deleted by all' : ''}
            </span>
          </GridCellBase>
        ),
      },
      */

      {
        field: 'decodedStatus',
        title: t('Status'),
        filter: 'text',
        filterable: true,
        columnMenu: StatusFilterCell,
        sortable: true,
        width: 80,
        component: (props: GridCellProps) => {
          const value = props.dataItem.decodedStatus;

          let cellStyle: React.CSSProperties | undefined;
          let textStyle: React.CSSProperties = {
            fontStyle: 'italic',
            display: 'block',
            width: '100%',
          };

          switch (value.replace(/\s+/g, ' ').trim().toLowerCase()) {
            case 'new':
              cellStyle = { backgroundColor: MESSAGE_COLORS.STATUS_NEW };
              break;
            case 'read':
              cellStyle = { backgroundColor: MESSAGE_COLORS.STATUS_READ };
              break;
            case 'viewed':
              cellStyle = { backgroundColor: MESSAGE_COLORS.STATUS_VIEWED };
              break;
            case 'replied':
              cellStyle = { backgroundColor: MESSAGE_COLORS.STATUS_REPLIED };
              break;
            case 'deleted by recipient':
              cellStyle = { backgroundColor: MESSAGE_COLORS.STATUS_DELBYRECIP };
              textStyle = { ...textStyle, color: 'black' };
              break;
            case 'deleted':
            case 'deleted by all':
              cellStyle = { backgroundColor: MESSAGE_COLORS.STATUS_DEL };
              textStyle = { ...textStyle, color: 'black' };
              break;
            case 'deleted by sender':
              cellStyle = { backgroundColor: MESSAGE_COLORS.STATUS_DELBYSENDER };
              textStyle = { ...textStyle, color: 'black' };
              break;
            default:
              cellStyle = { backgroundColor: MESSAGE_COLORS.STATUS_READ };
              break;
          }

          return (
            <GridCellBase {...props} style={cellStyle}>
              <span style={textStyle}>{value}</span>
            </GridCellBase>
          );
        },
      },

      /*----- Hide column for plain Status (numeric) -----
      {
        field: 'status',
        title: t('Status (raw)'),
        filter: 'numeric',
        sortable: true,
        filterable: true,
        width: 85,
        className: 'text-center',
        hidden: false,
      },
      */

      /* ------ HIDE Deleted (Cast) column ---------
      {
        field: 'isDeletedMsgCast',
        title: t('Deleted (Cast)'),
        filter: 'boolean',
        sortable: true,
        filterable: true,
        width: 135,
        className: 'text-center',
        hidden: false,
        component: (props: GridCellProps) => (
          <GridCellBase {...props}>
            <span style={{ display: 'block', textAlign: 'center', width: '100%' }}>
              {props.dataItem.isDeletedMsgCast ? 'Yes' : 'No'}
            </span>
          </GridCellBase>
        ),
      },
      */

      //FROM user (PERSON who authored message, not company)
      {
        field: 'createdByUserName',
        title: t('From'),
        filter: 'text',
        sortable: true,
        filterable: true,
        width: columnFormat1Size + 50,
      },

      {
        field: 'subject',
        title: t('Subject'),
        component: MessageSubject,
        dataType: 'string',
        filter: 'text',
        sortable: true,
        filterable: true,
        width: columnFormat1Size + 50,
        // disableCellTooltip: true,
      },

      {
        field: 'message',
        title: t('Message'),
        filter: 'text',
        sortable: true,
        filterable: true,
        width: columnFormat1Size + 210,
        className: 'message-column-cell',
        component: (props: GridCellProps) => (
          <GridCellBase {...props} className="message-column-cell">
            <span className="message-cell-hover-expand">{props.dataItem.message}</span>
          </GridCellBase>
        ),
      },

      {
        field: 'creationDate',
        title: t('Received On'),
        sortable: true,
        filterable: true,
        filter: 'date',
        width: columnFormat1Size - 30,
        component: (props: GridCellProps) => {
          // Sort on creationDate (full datetime), but filter on creationDateOnly (date only)
          const raw = props.dataItem.creationDate;
          const date = raw instanceof Date ? raw : raw ? new Date(raw) : null;
          const formatted =
            date && !isNaN(date.getTime()) ? format(date, 'MM/dd/yyyy hh:mm a') : '';
          return (
            <GridCellBase {...props} title={formatted}>
              <span>{formatted}</span>
            </GridCellBase>
          );
        },
      },
    ],
    [hasEditMessagePermission, t]
  );
  return viewMessageInboxColumns;
};
